// GrowthBook client singleton. Created at module load so the instance — and any
// forced feature values — exist before the first component reads a flag.
import { GrowthBook } from "@growthbook/growthbook-react";
import { trackExperimentViewed } from "../helpers/tracking";

/**
 * E2E feature-pinning seam.
 *
 * The E2E gate replays a committed action cache and asserts one specific funnel,
 * but a GrowthBook feature on a PERCENTAGE ROLLOUT hashes on anonymousId — and
 * every flow creates a fresh account, so each one draws an independent coin flip
 * and the gate goes red at random. Worse, a flow can only assert the arm it was
 * written for, so the other arm ships untested.
 *
 * Pinning fixes WHICH arm a flow runs in. It never weakens an assertion: each arm
 * gets a flow that asserts that arm's real behavior.
 *
 * Two layers, both gated on NEXT_PUBLIC_E2E_FORCED_FEATURES === '1' (set only by
 * the e2e workflow build, mirroring the E2E_FAKE_WALLET seam in next.config.js):
 *
 *   1. Build default — NEXT_PUBLIC_FORCED_FEATURES, a JSON {featureKey: value}
 *      map baked in at build time. Applies to every flow in the run.
 *   2. Per-flow override — window.__E2E_FORCED_FEATURES__, set by a flow's
 *      `init` via addInitScript (which runs before any app script), merged over
 *      the build default. This is what lets one build cover both arms.
 *
 * In a normal build NEXT_PUBLIC_E2E_FORCED_FEATURES is unset, so `enabled` folds
 * to a compile-time false and the whole block is dead-code-eliminated — the seam
 * cannot fire in production even if the other variables somehow leak in.
 *
 * @returns {Map<string, any>|null} pinned values, or null when the seam is off
 */
const forcedFeatures = () => {
  const enabled =
    process.env.NEXT_PUBLIC_E2E_FORCED_FEATURES === "1" &&
    process.env.NEXT_PUBLIC_ENVIRONMENT !== "production";

  if (!enabled) {
    return null;
  }

  const pins = {
    ...parseJsonMap(process.env.NEXT_PUBLIC_FORCED_FEATURES, "NEXT_PUBLIC_FORCED_FEATURES"),
    ...(typeof window !== "undefined"
      ? parseObjectMap(window.__E2E_FORCED_FEATURES__, "window.__E2E_FORCED_FEATURES__")
      : {})
  };

  const entries = Object.entries(pins);
  return entries.length ? new Map(entries) : null;
};

/**
 * parseJsonMap: JSON string -> plain object of feature pins.
 * Never throws — a malformed debug knob must not take the app down, so it warns
 * and contributes nothing, leaving remote values in place.
 *
 * @param {string|undefined} raw JSON text, e.g. '{"my-flag": false}'
 * @param {string} source name used in the warning
 * @returns {Object<string, any>}
 */
const parseJsonMap = (raw, source) => {
  if (!raw) {
    return {};
  }
  try {
    return parseObjectMap(JSON.parse(raw), source);
  } catch (error) {
    console.warn(`[growthbook] Ignoring malformed ${source}: ${error.message}`);
    return {};
  }
};

/**
 * parseObjectMap: validate an already-parsed value is a plain {key: value} map.
 *
 * @param {unknown} value candidate map
 * @param {string} source name used in the warning
 * @returns {Object<string, any>}
 */
const parseObjectMap = (value, source) => {
  if (value === undefined || value === null) {
    return {};
  }
  if (typeof value !== "object" || Array.isArray(value)) {
    console.warn(`[growthbook] Ignoring malformed ${source}: expected an object of {featureKey: value}`);
    return {};
  }
  return value;
};

export const growthbook = new GrowthBook({
  apiHost: "https://cdn.growthbook.io",
  clientKey: process.env.NEXT_PUBLIC_GROWTHBOOK_CLIENT_KEY,
  decryptionKey: process.env.NEXT_PUBLIC_GROWTHBOOK_DECRYPT_KEY,
  enableDevMode: process.env.NEXT_PUBLIC_ENVIRONMENT === "development",
  subscribeToChanges: true,
  trackingCallback: (experiment, result) => {
    trackExperimentViewed(experiment, result);
  }
});

// Applied before loadFeatures() so a pin wins from the very first read and
// survives later CDN refreshes (subscribeToChanges). Forcing does NOT flip
// growthbook.ready, so consumers that gate on readiness (e.g. /book's flagReady)
// still wait for the real load — which is what keeps SSR and the first client
// render in agreement.
const pinned = forcedFeatures();
if (pinned) {
  growthbook.setForcedFeatures(pinned);
}
